<template>
    <app-title :title="trans['messages.music']" />
    <!-- eslint-disable max-len -->
    <section ref="el" class="max-w-7xl mx-auto px-5">
        <div class="flex flex-col items-center sm:justify-between sm:flex-row rounded-3xl p-10 mt-20 mb-10 bg-slate-800/60 ring-1 ring-slate-300/10">
            <h2 class="text-2xl bg-linear-to-r from-indigo-200 via-sky-400 to-indigo-200 bg-clip-text text-transparent xl:text-3xl mb-5 sm:mb-0">
                {{ trans['messages.music'] }}
            </h2>
            <ol class="flex divide-x divide-slate-400">
                <li class="px-5">
                    <a class="link"
                       href="#"
                       @click.prevent="selectTrackAlbum()">
                        {{ trans['messages.album.plural'] }}
                    </a>
                </li>
                <li v-if="currentTrackAlbum" class="px-5">
                    {{ currentTrackAlbum.name }}
                </li>
                <li v-else class="px-5">
                    {{ trans['messages.please_select'] }}
                </li>
            </ol>
        </div>
        <div class="mb-20">
            <div v-if="trackAlbums.length && !currentTrackAlbum"
                 class="grid gap-10 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
                <div v-for="album in trackAlbums"
                     :key="album.id"
                     class="group relative flex flex-col bg-slate-800 border border-slate-300/10 rounded-3xl shadow">
                    <div class="absolute -inset-[2px] rounded-3xl border-2 border-transparent opacity-0 [background:linear-gradient(var(--color-slate-800),var(--color-slate-800))_padding-box,linear-gradient(to_top,var(--color-indigo-400),var(--color-cyan-400),var(--color-sky-500))_border-box] group-hover:opacity-100"></div>
                    <a class="relative rounded-t-3xl"
                       href="#"
                       @click.prevent="selectTrackAlbum(album)">
                        <img :src="album.thumbnail"
                             class="rounded-t-3xl"
                             alt="">
                    </a>
                    <div class="relative flex flex-1 flex-col items-center p-5">
                        {{ album.name }}
                    </div>
                </div>
            </div>
            <div v-else-if="!currentTrackAlbum" class="flex justify-center">
                {{ trans['messages.no_items'] }}
            </div>
            <div v-if="currentTrackAlbum && currentTrackAlbum.tracks.length"
                 class="grid gap-10 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
                <div v-for="current in currentTrackAlbum.tracks"
                     :id="`track-${current.id}`"
                     :key="current.id"
                     class="group relative flex flex-col bg-slate-800 border border-slate-300/10 rounded-3xl shadow">
                    <div class="absolute -inset-[2px] rounded-3xl border-2 border-transparent opacity-0 [background:linear-gradient(var(--color-slate-800),var(--color-slate-800))_padding-box,linear-gradient(to_top,var(--color-indigo-400),var(--color-cyan-400),var(--color-sky-500))_border-box] group-hover:opacity-100"
                         :class="{'opacity-100': id === current.id}"></div>
                    <a class="rounded-t-3xl relative"
                       href="#"
                       @click.prevent="toggleTrack(current)">
                        <span class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-white">
                            <pause-icon v-if="isPlaying(current)" class="h-8 w-8" />
                            <play-icon v-else class="h-8 w-8" />
                        </span>
                        <span v-if="isCurrent(current)" class="absolute bottom-10 left-1/2 -translate-x-1/2 text-sm text-white">
                            {{ timer(currentTrackTime) }} / {{ timer(currentTrackDuration) }}
                        </span>
                        <div v-if="isCurrent(current)"
                             class="progress absolute h-4 bottom-2 left-2 right-2 bg-slate-700 rounded"
                             @click.stop.prevent="seekTrackTo($event)">
                            <div class="h-4 bg-linear-to-r from-indigo-200 via-sky-400 to-indigo-200 rounded" :style="{width: trackPosition}"></div>
                        </div>
                        <img :src="currentTrackAlbum.thumbnail"
                             class="rounded-t-3xl"
                             alt="">
                    </a>
                    <div class="relative flex flex-1 flex-col items-center p-5">
                        <a :href="route('music', [app.locale, current.id])" target="_blank">
                            {{ current.name }}
                        </a>
                    </div>
                </div>
            </div>
            <div v-else-if="currentTrackAlbum" class="flex justify-center">
                {{ trans['messages.no_items'] }}
            </div>
        </div>
    </section>
    <!-- eslint-enable max-len -->
</template>

<script setup>
import {
    PlayIcon,
    PauseIcon
} from '@heroicons/vue/24/outline';

import {
    ref,
    computed,
    onMounted,
    defineProps,
    defineOptions
} from 'vue';

import Layout from '../../base/Layout';

require('lity');

const { id, trackAlbums } = defineProps({
    app: {
        type: Object,
        required: true
    },

    trackAlbums: {
        type: Array,
        required: true
    },

    trans: {
        type: Object,
        required: true
    },

    id: {
        type: Number,
        default: undefined
    }
});

defineOptions({
    layout: Layout
});

const el = ref();
const track = ref({});
const linkTrackAlbum = ref(undefined);
const currentTrackAlbum = ref(undefined);
const currentTrackTime = ref(0);
const currentTrackDuration = ref(0);

const trackPosition = computed(() => (currentTrackDuration.value
    ? `${currentTrackTime.value * (100 / currentTrackDuration.value)}%`
    : '0%'));

const isCurrent = newTrack => !_.isEmpty(track.value) && track.value.id === newTrack.id;
const isPlaying = newTrack => isCurrent(newTrack) && track.value.isPlaying;

const selectTrackAlbum = newTrackAlbum => {
    currentTrackAlbum.value = newTrackAlbum;
};

const scrollToLinkTrack = () => {
    trackAlbums.forEach(trackAlbum => _.forEach(trackAlbum.tracks, currentTrack => {
        if (id === currentTrack.id) {
            linkTrackAlbum.value = trackAlbum;
        }
    }));

    if (linkTrackAlbum.value) {
        selectTrackAlbum(linkTrackAlbum.value);
    }

    setTimeout(() => {
        document.querySelector(`#track-${id}`).scrollIntoView({
            behavior: 'smooth'
        });
    }, 100);
};

const toggleTrack = newTrack => {
    if (isPlaying(newTrack)) {
        document.dispatchEvent(new Event('track-pause'));
    } else if (isCurrent(newTrack)) {
        document.dispatchEvent(new Event('track-play'));
    } else {
        document.dispatchEvent(new CustomEvent('track-add', {
            detail: {
                track: newTrack,
                tracks: currentTrackAlbum.value.tracks
            }
        }));
    }
};

const seekTrackTo = e => {
    const bcr = el.value.querySelector('.progress').getBoundingClientRect();

    document.dispatchEvent(new CustomEvent('track-seek-to', {
        detail: {
            seekTo: Math.min(Math.max(0, (e.clientX - bcr.left) / bcr.width), 1)
        }
    }));
};

onMounted(() => {
    if (_.isNumber(id)) {
        scrollToLinkTrack();
    }
});

document.addEventListener('track', e => { track.value = e.detail; }, false);

document.addEventListener('track-timer', e => {
    currentTrackTime.value = e.detail.trackTime;
    currentTrackDuration.value = e.detail.trackDuration;
}, false);
</script>
